import React, { useEffect, useMemo, useState } from 'react';
import { Send, Loader2, Mail, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';

interface ContactType {
  id: string;
  label: string;
  mask: string;
  tag: string;
}

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export default function ContactSection() {
  const [types, setTypes] = useState<ContactType[]>([]);
  const [kind, setKind] = useState('fan');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ mask: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    api.get<{ types: ContactType[] }>('/public/contact/types')
      .then((r) => {
        setTypes(r.data.types || []);
        if (r.data.types?.[0]) setKind(r.data.types[0].id);
      })
      .catch(() => {});
  }, []);

  const activeType = useMemo(() => types.find((t) => t.id === kind), [types, kind]);
  const mask = activeType?.mask || 'info@ekubaproject.com';

  const emailOk = EMAIL_RE.test(email.trim());
  const canSend = name.trim().length > 1 && emailOk && message.trim().length > 4 && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ mask: string }>('/public/contact', {
        name: name.trim(),
        email: email.trim(),
        kind,
        subject: subject.trim(),
        message: message.trim(),
        website,
      });
      setSent({ mask: r.data.mask });
      toast.success('Message sent', {
        description: `We routed your note to ${r.data.mask}. Expect a reply from Ernesto within a few days.`,
        duration: 6000,
      });
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setTouched(false);
    } catch (err: any) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="ek-contact" aria-labelledby="contact-title" data-testid="contact-section">
      <div className="ek-contact-inner">
        <div className="ek-contact-head">
          <p className="ek-contact-kicker">GET IN TOUCH</p>
          <h2 id="contact-title" className="ek-contact-title">
            Let&apos;s make something happen.
          </h2>
          <p className="ek-contact-lede">
            Fan mail, press interviews, booking enquiries, licensing or distribution — pick the right desk below and it reaches the right pair of hands.
          </p>
        </div>

        {sent ? (
          <div className="ek-contact-sent" role="status" data-testid="contact-sent">
            <span className="ek-contact-sent-check" aria-hidden="true">
              <Check size={18} />
            </span>
            <div>
              <h3 className="ek-contact-sent-title">Message on its way to {sent.mask}</h3>
              <p className="ek-contact-sent-text">We read every note. Expect a reply from Ernesto within a few days.</p>
              <button
                type="button"
                onClick={() => setSent(null)}
                className="ek-contact-sent-again"
                data-testid="contact-send-another"
              >
                Send another
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="ek-contact-form" data-testid="contact-form">
            <label className="ek-contact-field ek-contact-field-full">
              <span className="ek-contact-label">I&apos;m writing as</span>
              <select
                className="ek-contact-input ek-contact-select"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                data-testid="contact-kind-select"
              >
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              <span className="ek-contact-hint">
                <Mail size={13} /> Routed to <strong>{mask}</strong>
              </span>
            </label>

            <label className="ek-contact-field">
              <span className="ek-contact-label">Your name</span>
              <input
                type="text"
                autoComplete="name"
                className={`ek-contact-input ${touched && name.trim().length < 2 ? 'is-invalid' : ''}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder="How should we address you?"
                data-testid="contact-name-input"
              />
            </label>

            <label className="ek-contact-field">
              <span className="ek-contact-label">Your email</span>
              <input
                type="email"
                autoComplete="email"
                className={`ek-contact-input ${touched && !emailOk ? 'is-invalid' : ''}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={254}
                placeholder="name@example.com"
                data-testid="contact-email-input"
              />
            </label>

            <label className="ek-contact-field ek-contact-field-full">
              <span className="ek-contact-label">
                Subject <span className="ek-contact-optional">· optional</span>
              </span>
              <input
                type="text"
                className="ek-contact-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={160}
                placeholder="e.g. Interview request for Midnight Asphalt"
                data-testid="contact-subject-input"
              />
            </label>

            <label className="ek-contact-field ek-contact-field-full">
              <span className="ek-contact-label">Message</span>
              <textarea
                rows={4}
                className={`ek-contact-input ek-contact-textarea ${touched && message.trim().length < 5 ? 'is-invalid' : ''}`}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={4000}
                placeholder="Tell us about the project, the show, the licensing deal — details help a lot."
                data-testid="contact-message-input"
              />
              <span className="ek-contact-hint">{message.length}/4000</span>
            </label>

            {/* honeypot */}
            <label className="ek-contact-honey" aria-hidden="true">
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>

            {error && (
              <div className="ek-contact-error" role="alert" data-testid="contact-error">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div className="ek-contact-footer">
              <p className="ek-contact-foot-note">
                By sending, you agree to our{' '}
                <a href="/legal/privacy">privacy policy</a>.
              </p>
              <button
                type="submit"
                className="ek-contact-send"
                disabled={!canSend}
                data-testid="contact-submit-button"
              >
                {busy ? <Loader2 size={16} className="ek-spin" /> : <Send size={16} />}
                {busy ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
